import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import FileUploader from '@/components/FileUploader';
import { ACCEPTED_FILES_CSV } from '@/constants/common';
import { useUploadAsset } from '@/pages/Asset/Asset.store';
import type { AssetUpload } from '@/pages/Assets';

import { ASSET_UPLOAD_DIALOG_DEFAULT_VALUES } from './AssetUploadDialog.constants';
import useStyles from './AssetUploadDialog.styles';
import type { EditAssetDialogProps } from './AssetUploadDialog.types';
import { downloadExampleCSV } from './AssetUploadDialog.utils';

const AssetUploadDialog = ({ open, onClose }: EditAssetDialogProps) => {
  const { classes } = useStyles();
  const { mutate: uploadAsset } = useUploadAsset();

  const form = useForm({
    defaultValues: ASSET_UPLOAD_DIALOG_DEFAULT_VALUES
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = form;

  const uploadType = useWatch({ control, name: 'uploadType' });

  const onSubmit = async (data: AssetUpload) => {
    try {
      uploadAsset(data);
      onClose();
    } catch (error) {
      console.error('Error saving asset:', error);
    }
  };

  const handleCancel = () => {
    reset(); // Reset form to default values
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Upload</DialogTitle>
      <IconButton
        className={classes.assetUploadDialogIconButtonCancel}
        onClick={handleCancel}
      >
        <FontAwesomeIcon size="xs" icon={faXmark} />
      </IconButton>
      <DialogContent sx={{ padding: '1.5rem' }}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form Fields */}
            <Stack
              className={classes.formFieldsContainer}
              direction="column"
              sx={{ justifyContent: 'flex-start' }}
            >
              {/* <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Upload Type
                </Typography>
                <Controller
                  name="uploadType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      sx={{ mt: 1, gap: 2, ml: '0.375rem' }}
                    >
                      <FormControlLabel
                        value="asset"
                        control={<Radio />}
                        label="Assets"
                      />
                      <FormControlLabel
                        value="ipMapping"
                        control={<Radio />}
                        label="IP address mapping"
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl> */}
              {uploadType === 'asset' && (
                <Stack direction="column" sx={{ ml: '-0.75rem' }} spacing={2}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center' }}
                    spacing={2}
                  >
                    <Controller
                      name="clear_existing"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Delete all previously uploaded assets before importing the new assets"
                        />
                      )}
                    />
                  </Stack>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center' }}
                    spacing={2}
                  >
                    <Controller
                      name="on_conflict_update"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Overwrite existing asset information (based on asset name)"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              )}
              {uploadType === 'asset' && (
                <Box sx={{ mb: 3 }}>
                  <FileUploader
                    acceptedTypes={ACCEPTED_FILES_CSV}
                    singleFile
                    errorMessage="Please upload a CSV file"
                    isRequired
                    titleButtonVariant="outlined"
                    titleText="Upload CSV"
                  />
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, fontSize: '0.875rem' }}
                  >
                    Click{' '}
                    <Button
                      onClick={downloadExampleCSV}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--mui-palette-primary-main)',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit'
                      }}
                    >
                      here
                    </Button>{' '}
                    to download an example CSV file.
                  </Typography>
                  <Typography sx={{ pb: 1, pt: 2 }} variant="h5">
                    CSV Format Notes:
                  </Typography>
                  <Typography>
                    Your CSV file should include the columns listed below with
                    the exact same names. Any additional columns will be
                    ignored.
                  </Typography>
                  <Table
                    sx={{ minWidth: 650 }}
                    size="small"
                    aria-label="a dense table"
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Required Column Names
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              fontStyle: 'italic',
                              fontSize: '0.75rem'
                            }}
                          >
                            asset_name, asset_type, asset_subtype, ip_address,
                            operating_system
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Optional Column Names
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              fontStyle: 'italic',
                              fontSize: '0.75rem'
                            }}
                          >
                            compliance_tags, criticality, description, domain,
                            last_seen_at, tags
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  <Typography sx={{ mt: 2 }}>
                    The <strong>last_seen_at</strong> column should use the
                    format <em>YYYY-MM-DD HH:MM:SS</em> (example: 2025-07-31
                    19:57:50). For valid values for columns such as{' '}
                    <strong>asset_type</strong>, <strong>asset_subtype</strong>,
                    and <strong>criticality</strong>, please check the{' '}
                    <Button
                      variant="text"
                      onClick={downloadExampleCSV}
                      sx={{ p: 0 }}
                    >
                      example CSV file
                    </Button>
                    . To add multiple tags, separate them with commas and wrap
                    the entire value in double quotes (example:
                    &quot;tag1,tag2&quot;).
                  </Typography>
                </Box>
              )}

              {uploadType === 'ipMapping' && (
                <FileUploader
                  acceptedTypes={ACCEPTED_FILES_CSV}
                  singleFile
                  errorMessage="Please upload a CSV file"
                  isRequired
                  titleButtonVariant="outlined"
                  titleText="Upload CSV"
                />
              )}
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem' }}>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetUploadDialog;
